import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Flame, Phone, Search, ShieldQuestion } from 'lucide-react';
import clsx from 'clsx';

import ClientsTabs from '@/components/ClientsTabs';
import { PartnerPill } from '@/components/PartnerPill';
import { apiErrorMessage, apiGet } from '@/lib/api';
import type { BackState } from '@/lib/back-nav';
import { formatDate } from '@/lib/format';
import { telHref } from '@/lib/phone';
import { pluralDays } from '@/lib/subscription';
import {
  KIND,
  MODERATION_LABEL,
  partnerExpiring,
  type PartnerKind,
  type PartnerRow,
} from '@/lib/partners';

type Filter = 'hot' | 'pending';

// Список между заходами в карточку — в памяти, как у заведений: «назад» рисует сразу.
const cache = new Map<PartnerKind, PartnerRow[]>();

/** Поставщики или оптовики — руководителю, только просмотр. */
export default function Partners({ kind }: { kind: PartnerKind }) {
  const cfg = KIND[kind];
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<PartnerRow[] | null>(() => cache.get(kind) ?? null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const raw = params.get('f');
  const filter: Filter | null = raw === 'hot' || raw === 'pending' ? raw : null;

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(kind) ?? null;
    setItems(cached);
    setError(null);
    void apiGet<{ results: PartnerRow[]; total: number }>(`${cfg.path}?limit=200`, { silent: true })
      .then((res) => {
        if (cancelled) return;
        cache.set(kind, res.results);
        setItems(res.results);
      })
      .catch((err) => {
        if (!cancelled && !cached) setError(apiErrorMessage(err, `Не удалось загрузить: ${cfg.title.toLowerCase()}`));
      });
    return () => {
      cancelled = true;
    };
  }, [kind, cfg.path, cfg.title]);

  const updateParam = (key: string, value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const rows = items ?? [];
  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      rows.filter((r) => {
        if (filter === 'hot' && !partnerExpiring(r)) return false;
        if (filter === 'pending' && r.status !== 'pending') return false;
        if (needle && !matches(r, needle)) return false;
        return true;
      }),
    [rows, filter, needle],
  );
  const hot = rows.filter(partnerExpiring).length;
  const pending = rows.filter((r) => r.status === 'pending').length;
  const backState: BackState = { backTo: location.pathname + location.search, backSteps: 1, backLabel: cfg.back };
  const cardHref = (id: string) => `${cfg.path}/${id}`;

  return (
    <div className="max-w-6xl space-y-4">
      <ClientsTabs />
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">{cfg.title}</h1>
        <p className="mt-1 text-sm text-ink/55">
          {items === null
            ? 'Загрузка…'
            : rows.length
              ? `${visible.length !== rows.length ? `${visible.length} из ` : ''}${rows.length} на платформе${hot ? ` · ${hot} с истекающим сроком` : ''}`
              : cfg.empty}
        </p>
      </div>

      {rows.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/35" />
            <input
              className="input !pl-9"
              type="search"
              enterKeyHint="search"
              placeholder={kind === 'supplier' ? 'Название, email, город или ИНН' : 'Название, email, город или телефон'}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                updateParam('q', e.target.value.trim() || null);
              }}
              aria-label="Поиск"
            />
          </div>
          <div className="flex gap-2">
            <FilterButton
              active={filter === 'hot'}
              onClick={() => updateParam('f', filter === 'hot' ? null : 'hot')}
              tone="rose"
            >
              <Flame size={16} /> Горят{hot ? ` · ${hot}` : ''}
            </FilterButton>
            {pending > 0 || filter === 'pending' ? (
              <FilterButton
                active={filter === 'pending'}
                onClick={() => updateParam('f', filter === 'pending' ? null : 'pending')}
                tone="amber"
              >
                <ShieldQuestion size={16} /> На модерации · {pending}
              </FilterButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? <div className="card p-4 text-sm text-ink/70">{error}</div> : null}
      {items !== null && rows.length > 0 && !visible.length ? (
        <div className="card p-6 text-sm text-ink/55">Под фильтр ничего не попало.</div>
      ) : null}

      {visible.length > 0 ? (
        <>
          {/* Телефон */}
          <div className="space-y-3 md:hidden">
            {visible.map((r) => {
              const tel = telHref(r.phone);
              return (
                <div key={r.id} className="card overflow-hidden">
                  <Link to={cardHref(r.id)} state={backState} className="block space-y-3 p-4 active:bg-paper">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium">{r.name}</div>
                        <div className="mt-0.5 truncate text-xs text-ink/45">
                          {[r.city, r.email].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <PartnerPill row={r} />
                    </div>
                    <div className="flex items-baseline justify-between gap-3 border-t border-line/60 pt-3 text-sm">
                      <span className="text-xs text-ink/45">Осталось</span>
                      <DaysLeft row={r} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-ink/50">
                      <span className="min-w-0">
                        {r.status !== 'active' ? `${MODERATION_LABEL[r.status]} · ` : ''}
                        {activity(kind, r)} · вход {r.lastLoginAt ? formatDate(r.lastLoginAt) : '—'}
                      </span>
                      <ChevronRight size={16} className="shrink-0 text-ink/30" />
                    </div>
                  </Link>
                  {tel && r.phone ? (
                    <a
                      href={tel}
                      className="flex items-center gap-3 border-t border-line/60 px-4 py-2.5 text-sm active:bg-paper"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                        <Phone size={15} />
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">Позвонить</span>
                      <span className="shrink-0 text-xs tabular-nums text-ink/55">{r.phone}</span>
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Компьютер */}
          <div className="card hidden overflow-x-auto md:block">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-ink/50">
                  <th className="px-4 py-3">{KIND[kind].one}</th>
                  <th className="px-4 py-3">Модерация</th>
                  <th className="px-4 py-3">Подписка</th>
                  <th className="px-4 py-3">Осталось</th>
                  <th className="px-4 py-3">Активность</th>
                  <th className="px-4 py-3">Звонок</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const tel = telHref(r.phone);
                  return (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b border-line/60 last:border-0 hover:bg-paper"
                      onClick={() => navigate(cardHref(r.id), { state: backState })}
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={cardHref(r.id)}
                          state={backState}
                          className="font-medium hover:text-orange"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.name}
                        </Link>
                        <div className="text-xs text-ink/45">
                          {r.city ?? '—'} · с {formatDate(r.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            'text-sm',
                            r.status === 'pending' ? 'font-medium text-amber-700' : r.status === 'blocked' ? 'text-rose-700' : 'text-ink/70',
                          )}
                        >
                          {MODERATION_LABEL[r.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <PartnerPill row={r} />
                        <div className="mt-1 text-xs text-ink/45">{r.autoRenew ? 'автопродление' : 'без автопродления'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <DaysLeft row={r} />
                        {r.accessEndsAt ? <div className="text-xs text-ink/45">до {formatDate(r.accessEndsAt)}</div> : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{activity(kind, r)}</div>
                        <div className="text-xs text-ink/45">вход {r.lastLoginAt ? formatDate(r.lastLoginAt) : '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        {tel && r.phone ? (
                          <a
                            href={tel}
                            onClick={(e) => e.stopPropagation()}
                            className="-mx-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Phone size={14} className="shrink-0" />
                            <span className="tabular-nums">{r.phone}</span>
                          </a>
                        ) : (
                          <span className="text-ink/30">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function FilterButton({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone: 'rose' | 'amber';
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'btn shrink-0 border',
        active
          ? tone === 'rose'
            ? 'border-rose-200 bg-rose-50 text-rose-700'
            : 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-line bg-white text-ink/70 hover:bg-paper',
      )}
    >
      {children}
    </button>
  );
}

function DaysLeft({ row }: { row: PartnerRow }) {
  if (row.accessState === 'blocked') return <span className="text-sm font-medium text-rose-600">Доступ закрыт</span>;
  if (row.daysLeft == null) return <span className="text-sm text-ink/40">Без срока</span>;
  const cls = row.daysLeft <= 3 ? 'text-amber-600' : 'text-ink';
  return (
    <span className={clsx('text-sm font-medium', cls)}>
      {row.daysLeft === 0 ? 'Заканчивается сегодня' : `${row.daysLeft} ${pluralDays(row.daysLeft)}`}
    </span>
  );
}

function activity(kind: PartnerKind, r: PartnerRow): string {
  return kind === 'supplier'
    ? `товаров ${r.productsCount ?? 0}`
    : `товаров ${r.itemsCount ?? 0} · продаж ${r.salesCount ?? 0}`;
}

function matches(r: PartnerRow, needle: string): boolean {
  if (`${r.name} ${r.email} ${r.city ?? ''} ${r.legalName ?? ''} ${r.inn ?? ''}`.toLowerCase().includes(needle)) {
    return true;
  }
  const digits = needle.replace(/\D/g, '');
  return digits.length >= 3 && (r.phone?.replace(/\D/g, '') ?? '').includes(digits);
}
